import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Calculator, TrendingUp, Award, Star, Trophy, Medal,
  DollarSign, Percent, Users, ArrowRight, CheckCircle2
} from 'lucide-react';
import { api } from '@/lib/api';

interface Tier {
  level: string;
  minPoints: number;
  maxPoints: number;
  discountRate: number;
  discountPercentage: number;
  color: string;
}

interface Customer {
  id: number;
  fullName: string;
  name?: string;
  totalPoints: number;
  loyaltyLevel: string;
}

export default function LoyaltyPricingPage() {
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [basePrice, setBasePrice] = useState<number>(1000);
  const [discountInfo, setDiscountInfo] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadTiers();
    loadCustomers();
  }, []);

  const loadTiers = async () => {
    try {
      const res = await api.get('/api/loyaltypricing/tiers');
      if (res.data.success) {
        setTiers(res.data.data);
      }
    } catch (error) {
      console.error('Error loading tiers:', error);
    }
  };

  const loadCustomers = async () => {
    try {
      const res = await api.get('/api/customers?pageSize=100');
      if (res.data.success) {
        const data = res.data.data;
        setCustomers(Array.isArray(data) ? data : (data?.items || []));
      }
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const calculateDiscount = async () => {
    if (!selectedCustomerId) {
      alert('يرجى اختيار عميل');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/api/loyaltypricing/calculate', {
        customerId: selectedCustomerId,
        basePrice: basePrice
      });
      if (res.data.success) {
        setDiscountInfo(res.data.data);
      }
    } catch (error) {
      console.error('Error calculating discount:', error);
      alert('حدث خطأ أثناء الحساب');
    } finally {
      setLoading(false);
    }
  };

  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'Platinum': return <Trophy className="w-8 h-8" />;
      case 'Gold': return <Award className="w-8 h-8" />;
      case 'Silver': return <Medal className="w-8 h-8" />;
      default: return <Star className="w-8 h-8" />;
    }
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'Platinum': return 'from-purple-500 to-indigo-600';
      case 'Gold': return 'from-yellow-400 to-amber-600';
      case 'Silver': return 'from-gray-300 to-gray-500';
      default: return 'from-orange-400 to-orange-600';
    }
  };

  return (
    <div className="p-6 space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
              <Percent className="w-6 h-6 text-white" />
            </div>
            الخصومات التلقائية
          </h1>
          <p className="text-muted-foreground mt-1">حاسبة الخصومات حسب مستوى الولاء</p>
        </div>
      </div>

      {/* Tiers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {tiers.map((tier) => (
          <Card key={tier.level} className={`overflow-hidden border-2 ${tier.discountPercentage > 0 ? 'border-green-200' : 'border-gray-200'}`}>
            <div className={`bg-gradient-to-br ${getLevelColor(tier.level)} p-6 text-white`}>
              <div className="flex items-center justify-between mb-4">
                {getLevelIcon(tier.level)}
                {tier.discountPercentage > 0 && (
                  <Badge className="bg-white/20 text-white border-white/30">
                    -{tier.discountPercentage}%
                  </Badge>
                )}
              </div>
              <h3 className="text-2xl font-bold mb-1">{tier.level}</h3>
              <p className="text-white/80 text-sm">
                {tier.minPoints.toLocaleString()} - {tier.maxPoints === 999999 ? '+∞' : tier.maxPoints.toLocaleString()} نقطة
              </p>
            </div>
            <CardContent className="p-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">نسبة الخصم:</span>
                  <span className="font-bold text-lg">{tier.discountPercentage}%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">النقاط المطلوبة:</span>
                  <span className="font-semibold">{tier.minPoints.toLocaleString()}+</span>
                </div>
                {tier.discountPercentage > 0 && (
                  <div className="pt-2 mt-2 border-t">
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      يتم تطبيقه تلقائياً
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Calculator */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-blue-500" />
            حاسبة الخصم
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">اختر العميل</label>
              <select
                value={selectedCustomerId || ''}
                onChange={(e) => setSelectedCustomerId(e.target.value ? Number(e.target.value) : null)}
                className="w-full px-4 py-2 border rounded-lg bg-white"
              >
                <option value="">-- اختر عميل --</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.fullName} ({c.loyaltyLevel} - {c.totalPoints} نقطة)
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">السعر الأساسي (ج.م)</label>
              <Input
                type="number"
                value={basePrice}
                onChange={(e) => setBasePrice(Number(e.target.value))}
                min="0"
                step="0.01"
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={calculateDiscount}
                disabled={!selectedCustomerId || loading}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                {loading ? 'جاري الحساب...' : 'احتسب'}
              </Button>
            </div>
          </div>

          {/* Result */}
          {discountInfo && (
            <div className="mt-6 p-6 bg-gradient-to-l from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-blue-600" />
                نتيجة الحساب
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-white rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">العميل</p>
                  <p className="font-bold text-sm">{discountInfo.customerName}</p>
                  <Badge className="mt-2 text-xs">{discountInfo.level}</Badge>
                </div>
                <div className="p-4 bg-white rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">السعر الأساسي</p>
                  <p className="font-bold text-lg">{discountInfo.basePrice.toFixed(2)} ج.م</p>
                </div>
                <div className="p-4 bg-white rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">نسبة الخصم</p>
                  <p className="font-bold text-lg text-green-600">{discountInfo.discountPercentage}%</p>
                </div>
                <div className="p-4 bg-white rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">مقدار الخصم</p>
                  <p className="font-bold text-lg text-red-600">-{discountInfo.discountAmount.toFixed(2)} ج.م</p>
                </div>
              </div>
              <div className="mt-4 p-4 bg-green-100 rounded-lg border-2 border-green-300">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-700 mb-1">السعر النهائي بعد الخصم</p>
                    <p className="text-3xl font-bold text-green-800">{discountInfo.finalPrice.toFixed(2)} ج.م</p>
                  </div>
                  <div className="text-left">
                    <p className="text-sm text-green-700 mb-1">التوفير</p>
                    <p className="text-2xl font-bold text-green-800">{discountInfo.savings.toFixed(2)} ج.م</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* How it works */}
      <Card>
        <CardHeader>
          <CardTitle>كيف يعمل النظام؟</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <span className="font-bold text-blue-600">1</span>
              </div>
              <div>
                <h4 className="font-semibold mb-1">اكتساب النقاط</h4>
                <p className="text-sm text-gray-600">كل شحنة تكسب العميل نقاط حسب قيمتها</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <span className="font-bold text-green-600">2</span>
              </div>
              <div>
                <h4 className="font-semibold mb-1">الترقية التلقائية</h4>
                <p className="text-sm text-gray-600">عند وصول النقاط المطلوبة، يترقى العميل تلقائياً</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                <span className="font-bold text-purple-600">3</span>
              </div>
              <div>
                <h4 className="font-semibold mb-1">تطبيق الخصم</h4>
                <p className="text-sm text-gray-600">يتم تطبيق الخصم تلقائياً عند إنشاء الشحنات</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
